import i18nFactory from '@yandex-int/i18n';
import { PopupDirection } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { AccessRights } from '../../typings/common';
import { Product, ProductsPageStates } from '../../typings/products';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { DirectFormInput } from '../DirectFormInput/DirectFormInput';
import { EditableCell } from '../EditableCell/EditableCell';
import { InputFieldType } from '../InputField/InputField';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';

import './ProductDescriptionCell.css';
import * as keyset from './ProductDescriptionCell.i18n';

const BLOCK = 'product-description-cell';
const DIRECTIONS: PopupDirection[] = ['top-center', 'bottom-center'];

const i18n = i18nFactory(keyset);

export interface ProductDescriptionCellProps {
    product: Product;
    pageStatus: ProductsPageStates;
    onSave: (product: Product) => void;
    onCancel: (product: Product) => void;
    onEdit: (product: Product) => void;
    isGridScrolling: boolean;
    accessRights: AccessRights;
}

interface State {
    description: string;
    editing: boolean;
}

export class ProductDescriptionCell extends React.Component<ProductDescriptionCellProps, State> {
    constructor(props: ProductDescriptionCellProps) {
        super(props);

        const {
            product: { description, isSaved }
        } = this.props;

        this.state = {
            description: description,
            editing: !isSaved
        };
    }

    render() {
        return <div className={Bem.cls(BLOCK)}>{this.renderBody()}</div>;
    }

    public componentDidUpdate() {
        if (!this.state.editing) {
            return;
        }

        const {
            product: { description, isSaved },
            isGridScrolling,
            accessRights: { canEditInternalAdManagers }
        } = this.props;

        if (isSaved) {
            this.setState({
                editing: false,
                description: description
            });

            return;
        }

        if (isGridScrolling && canEditInternalAdManagers) {
            this.onCancel();
        }
    }

    private renderBody = () => {
        const {
            product: { description },
            isGridScrolling,
            accessRights: { canEditInternalAdProducts }
        } = this.props;

        if (!canEditInternalAdProducts) {
            return <div>{description}</div>;
        }

        return (
            <>
                <EditableCell isActive={this.state.editing} onClick={this.onEdit} cls={Bem.cls(BLOCK, 'description')}>
                    {description}
                </EditableCell>
                {this.state.editing && (
                    <PopupDecorator
                        anchor={this}
                        visible={!isGridScrolling}
                        target={'anchor'}
                        directions={DIRECTIONS}
                        hasTail={true}
                        header={i18n('description')}
                        footer={this.renderFooter()}
                        onOutsideClick={this.onCancel}
                        cls={Bem.cls(BLOCK, 'popup')}
                    >
                        <DirectFormInput
                            theme={'normal'}
                            size={'xs'}
                            text={this.state.description}
                            cls={Bem.cls(BLOCK, 'textarea')}
                            inputType={InputFieldType.TEXTAREA}
                            valid={true}
                            onChange={this.onChangeDescription}
                        />
                    </PopupDecorator>
                )}
            </>
        );
    };

    private renderFooter = () => {
        const {
            pageStatus,
            product: { description }
        } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'actions')}>
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'action')}
                    theme={'action'}
                    size={'s'}
                    text={i18n('save')}
                    onClick={this.onSaveDescription}
                    progress={pageStatus === ProductsPageStates.ACTION}
                    disabled={description === this.state.description.trim()}
                />
                <ButtonDecorator
                    theme={'normal'}
                    size={'s'}
                    text={i18n('cancel')}
                    onClick={this.onCancel}
                    disabled={pageStatus === ProductsPageStates.ACTION}
                />
            </div>
        );
    };

    private onEdit = () => {
        const { onEdit, product } = this.props;

        this.setState({
            editing: true,
            description: product.description
        });

        onEdit(product);
    };

    private onCancel = () => {
        const { onCancel, product } = this.props;

        this.setState({
            editing: false,
            description: product.description
        });

        onCancel(product);
    };

    private onChangeDescription = (text: string) => {
        this.setState({
            description: text
        });
    };

    private onSaveDescription = () => {
        const { onSave, product } = this.props;

        onSave({
            ...product,
            description: this.state.description
        });
    };
}
